import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { appRoutes } from "routes";
import { FACE_PAGE_FLOWS, DATE_TYPE_QUERIES, ALGOLIA_FILTERS, TEMPLATE_GROUP_TYPE_NAMES } from "enums";
import { useFaces, useRouter, useAlgolia, useSettings, useUsers } from "hooks";
import PersonPresenter from "presenters/PersonPresenter";
import FacePresenter from "presenters/FacePresenter";
import SearchBoxGeneric from "components/SearchBoxGeneric";
import SearchResults from "components/SearchResults";
import CalendarDetailModal from "components/CalendarDetailModal";
import AlgoliaSearchWrapper from "components/AlgoliaSearchWrapper";

const noGifs = `NOT ${ALGOLIA_FILTERS.templateGroupTypeName}:"${TEMPLATE_GROUP_TYPE_NAMES.starringYouGif}"`;
const noSUs = `NOT ${ALGOLIA_FILTERS.templateGroupTypeName}:"${TEMPLATE_GROUP_TYPE_NAMES.straightUpEcard}"`;
const noMorphables = `NOT ${ALGOLIA_FILTERS.templateGroupTypeName}:"${TEMPLATE_GROUP_TYPE_NAMES.starringYouAI}"`;
const castOne = `${ALGOLIA_FILTERS.castNumOptions} = 1`;
const filters = `${noGifs} AND ${noSUs} AND ${noMorphables} AND ${castOne}`;

const AlgoliaCalendar = ({ person, dateType, dateToCelebrate, onClose, context }) => {
  const { setFaceToAddJaw, setPhoto, setMode } = useFaces();
  const { currentUserLoaded } = useUsers();
  const { pushWithSave } = useRouter();
  const face = PersonPresenter.defaultHead(person);
  const {
    settings: { algolia },
  } = useSettings();
  const { createInitialUiStateFromSingle } = useAlgolia();

  const initialQueryObject = useMemo(() => {
    if (!currentUserLoaded) return undefined;

    return createInitialUiStateFromSingle(algolia.indexName, {
      query: { query: DATE_TYPE_QUERIES[dateType] },
      configure: { filters, analytics: false, clickAnalytics: false, hitsPerPage: undefined },
    });
  }, [currentUserLoaded, dateType]);

  useEffect(() => {
    if (!FacePresenter.jaw(face)) {
      setFaceToAddJaw(face);
      setPhoto(FacePresenter.asset(face));
      setMode(FACE_PAGE_FLOWS.JAW_CUT_MISSING);
      pushWithSave(appRoutes.facePath());
    }
  }, []);

  if (!initialQueryObject || !currentUserLoaded) return null;

  return (
    <AlgoliaSearchWrapper initialUiState={initialQueryObject} context={context}>
      <SearchBoxGeneric
        initialQueryObject={initialQueryObject[algolia.indexName]}
        resultsSlot={SearchResults}
        resultsSlotProps={{
          WrappedComponent: CalendarDetailModal,
          person,
          dateType,
          dateToCelebrate,
          onClose,
        }}
      />
    </AlgoliaSearchWrapper>
  );
};

AlgoliaCalendar.propTypes = {
  person: PersonPresenter.shape().isRequired,
  dateType: PropTypes.string.isRequired,
  dateToCelebrate: PropTypes.string,
  onClose: PropTypes.func,
  context: PropTypes.string,
};

export default AlgoliaCalendar;
